import React, {useCallback, useRef} from 'react';
import {LiveSessionSummary} from '../../../../services/liveSessionsApi';
import HomeErrorMessage from './HomeErrorMessage';
import {REEL_PLACEHOLDER_BACKGROUNDS} from '../constants';
import {formatViewCount, getAssetUrl, getDisplayInitial} from '../utils';
import styles from '../Home.module.css';

const liveHeadingIconUrl = `${process.env.PUBLIC_URL}/icons/live-fill.svg`;
const liveBackgroundUrl = `${process.env.PUBLIC_URL}/live_background.png`;
const liveFillIconUrl = `${process.env.PUBLIC_URL}/icons/live-fill.svg`;
const viewFillIconUrl = `${process.env.PUBLIC_URL}/icons/view-fill.svg`;
const scrollArrowIconUrls = {
  left: `${process.env.PUBLIC_URL}/icons/arrow-left-bold.svg`,
  right: `${process.env.PUBLIC_URL}/icons/arrow-right-bold.svg`,
};

interface LiveNowSectionProps {
  sessions: LiveSessionSummary[];
  sessionsLoading: boolean;
  sessionsError: string | null;
  onRetry: () => void;
  onStartLive: () => void;
  onOpenSession: (session: LiveSessionSummary) => void;
}

const formatLiveMeta = (session: LiveSessionSummary) => {
  if (session.status === 'starting') {
    return 'Starting now';
  }

  const startedAtTimestamp = session.startedAt ? Date.parse(session.startedAt) : NaN;

  if (!Number.isFinite(startedAtTimestamp)) {
    return 'Live now';
  }

  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - startedAtTimestamp) / 60000));

  if (elapsedMinutes < 1) {
    return 'Live now';
  }

  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m live`;
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  const remainingMinutes = elapsedMinutes % 60;

  return remainingMinutes > 0
    ? `${elapsedHours}h ${remainingMinutes}m live`
    : `${elapsedHours}h live`;
};

const formatViewerCount = (viewerCount: number) => formatViewCount(viewerCount);

const getLiveSessionPreviewUrl = (session: LiveSessionSummary) => (
  getAssetUrl(session.previewImageUrl)
);

const LiveSessionCard: React.FC<{
  session: LiveSessionSummary;
  index: number;
  onOpen: (session: LiveSessionSummary) => void;
}> = React.memo(({
  session,
  index,
  onOpen,
}) => {
  const [previewImageFailed, setPreviewImageFailed] = React.useState(false);
  const [avatarImageFailed, setAvatarImageFailed] = React.useState(false);
  const fallbackBackground = REEL_PLACEHOLDER_BACKGROUNDS[index % REEL_PLACEHOLDER_BACKGROUNDS.length];
  const rawPreviewImageUrl = getLiveSessionPreviewUrl(session);
  const showPreviewImage = Boolean(rawPreviewImageUrl) && !previewImageFailed;
  const formattedViewerCount = formatViewerCount(session.viewerCount);
  const authorAvatarUrl = getAssetUrl(session.broadcaster.profilePicture);
  const showAuthorAvatarImage = Boolean(authorAvatarUrl) && !avatarImageFailed;
  const cardStyle = {
    '--live-card-fallback-background': fallbackBackground,
  } as React.CSSProperties;

  React.useEffect(() => {
    setPreviewImageFailed(false);
  }, [rawPreviewImageUrl]);

  React.useEffect(() => {
    setAvatarImageFailed(false);
  }, [authorAvatarUrl]);

  return (
    <button
      type="button"
      className={styles.liveNowCard}
      style={cardStyle}
      onClick={() => onOpen(session)}
    >
      {showPreviewImage && (
        <img
          src={rawPreviewImageUrl}
          alt=""
          aria-hidden="true"
          className={styles.liveNowPreviewImage}
          loading="lazy"
          onError={() => setPreviewImageFailed(true)}
        />
      )}
      <div className={styles.liveNowPreviewOverlay} />

      <div className={styles.liveNowBadgeRow}>
        <span className={`${styles.liveNowBadge} ${styles.liveNowBadgePrimary}`}>
          Live
        </span>
        <span
          className={`${styles.liveNowBadge} ${styles.liveNowBadgeSecondary}`}
          aria-label={`${formattedViewerCount} viewers`}
        >
          <span
            className={styles.liveNowViewerIcon}
            style={{'--live-now-viewer-icon-url': `url("${viewFillIconUrl}")`} as React.CSSProperties}
            aria-hidden="true"
          />
          <span className={styles.liveNowViewerCount}>{formattedViewerCount}</span>
        </span>
      </div>

      <div className={styles.liveNowGlow} />

      <div className={styles.liveNowCardFooter}>
        <div className={styles.liveNowCardBody}>
          <div className={styles.liveNowAuthorAvatar}>
            {showAuthorAvatarImage ? (
              <img
                src={authorAvatarUrl}
                alt={`${session.broadcaster.name} avatar`}
                className={styles.liveNowAuthorAvatarImage}
                loading="lazy"
                onError={() => setAvatarImageFailed(true)}
              />
            ) : (
              getDisplayInitial(session.broadcaster.name)
            )}
          </div>
          <div className={styles.liveNowAuthorMeta}>
            <span className={styles.liveNowAuthorName}>{session.broadcaster.name}</span>
            <span className={styles.liveNowAuthorStatus}>{formatLiveMeta(session)}</span>
          </div>
        </div>
        <p className={styles.liveNowTitle}>{session.title}</p>
        <span className={styles.liveNowAction}>Watch now</span>
      </div>
    </button>
  );
});

const ScrollChevron: React.FC<{direction: 'left' | 'right'}> = ({direction}) => (
  <span
    className={styles.scrollButtonIcon}
    style={{'--home-scroll-arrow-icon-url': `url("${scrollArrowIconUrls[direction]}")`} as React.CSSProperties}
    aria-hidden="true"
  />
);

const LiveNowSection: React.FC<LiveNowSectionProps> = ({
  sessions,
  sessionsLoading,
  sessionsError,
  onRetry,
  onStartLive,
  onOpenSession,
}) => {
  const sessionsScrollRef = useRef<HTMLDivElement>(null);

  const scrollSessions = useCallback((direction: 'left' | 'right') => {
    if (!sessionsScrollRef.current) {
      return;
    }

    const scrollAmount = 260;
    const nextScrollLeft = direction === 'left'
      ? sessionsScrollRef.current.scrollLeft - scrollAmount
      : sessionsScrollRef.current.scrollLeft + scrollAmount;

    sessionsScrollRef.current.scrollTo({
      left: nextScrollLeft,
      behavior: 'smooth',
    });
  }, []);

  const disableScrollControls = sessionsLoading || sessions.length === 0;

  return (
    <div
      id="live-now"
      className={`${styles.reelsSection} ${styles.liveNowSection}`.trim()}
      style={{'--live-now-section-background-url': `url("${liveBackgroundUrl}")`} as React.CSSProperties}
    >
      <div className={styles.sectionInner}>
        <div className={`${styles.sectionHeader} ${styles.liveNowSectionHeader}`}>
          <div className={styles.liveNowHeading}>
            <h2 className={styles.liveNowHeadingTitle}>
              <span
                className={styles.liveNowHeadingIcon}
                style={{'--live-now-heading-icon-url': `url("${liveHeadingIconUrl}")`} as React.CSSProperties}
                aria-hidden="true"
              />
              <span>Live Now</span>
            </h2>
            <p className={styles.sectionSubtitle}>Catch creators live in real time.</p>
          </div>
          <div className={`${styles.sectionActions} ${styles.liveNowHeaderActions}`}>
            <button
              type="button"
              className={`${styles.modalButton} ${styles.modalButtonPrimary} ${styles.scrollButton}`}
              aria-label="Scroll live sessions left"
              disabled={disableScrollControls}
              onClick={() => scrollSessions('left')}
            >
              <ScrollChevron direction="left" />
            </button>
            <button
              type="button"
              className={`${styles.modalButton} ${styles.modalButtonPrimary} ${styles.scrollButton}`}
              aria-label="Scroll live sessions right"
              disabled={disableScrollControls}
              onClick={() => scrollSessions('right')}
            >
              <ScrollChevron direction="right" />
            </button>
          </div>
        </div>

        {sessionsLoading && (
          <div className={styles.sectionMessage}>Loading live sessions...</div>
        )}

        {sessionsError && (
          <div className={styles.sectionErrorRow}>
            <p className={styles.sectionErrorText}>
              <HomeErrorMessage message={sessionsError} />
            </p>
            <button type="button" className={styles.primaryAction} onClick={onRetry}>
              Retry
            </button>
          </div>
        )}

        {!sessionsLoading && !sessionsError && sessions.length === 0 && (
          <div className={styles.liveNowEmptyState}>
            <div className={styles.liveNowEmptyContent}>
              <span className={styles.liveNowOfflineBadge}>
                <span className={styles.liveNowOfflineDot} aria-hidden="true" />
                No live sessions
              </span>
              <h3 className={styles.liveNowEmptyTitle}>No one is live right now</h3>
              <p className={styles.liveNowEmptyDescription}>
                Be the first to go live and connect with your audience.
              </p>
              <div className={styles.liveNowEmptyActions}>
                <button
                  type="button"
                  className={`${styles.modalButton} ${styles.modalButtonPrimary} ${styles.liveNowEmptyButton}`}
                  onClick={onStartLive}
                >
                  <span
                    className={styles.liveNowButtonIcon}
                    style={{'--live-now-button-icon-url': `url("${liveFillIconUrl}")`} as React.CSSProperties}
                    aria-hidden="true"
                  />
                  Start Live
                </button>
              </div>
            </div>
          </div>
        )}

        {!sessionsLoading && !sessionsError && sessions.length > 0 && (
          <div ref={sessionsScrollRef} className={styles.reelsScroller}>
            {sessions.map((session, index) => (
              <LiveSessionCard
                key={session.id}
                session={session}
                index={index}
                onOpen={onOpenSession}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(LiveNowSection);
